import { useState, type FormEvent } from 'react';

const SUBMIT = 'https://readdy.ai/api/form/dagl9ajep053ijub2cj0';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('website_alt') || '').trim();

    if (honeypot) {
      setStatus('success');
      setMessage('已收到你的订阅，感谢关注！');
      return;
    }

    const email = String(fd.get('email') || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('请填写有效的邮箱地址后再提交。');
      return;
    }
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });

    const payload: string[] = [];
    fd.forEach((val, key) => {
      if (key === 'website_alt') return;
      payload.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
    });

    setStatus('loading');
    try {
      const res = await fetch(SUBMIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.join('&'),
      });
      const text = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        /* ignore */
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const okCode = parsed?.code === 'OK' && res.ok;
      if (okCode) {
        setStatus('success');
        setMessage('订阅成功！最新吃瓜资讯马上就会送达你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，请检查后重试。');
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 md:px-6 pt-16 md:pt-24">
      <div className="rounded-3xl overflow-hidden border border-background-200/70 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* info */}
          <div className="lg:col-span-2 p-6 md:p-10 bg-primary-500 text-background-50 relative overflow-hidden">
            <div className="absolute -bottom-12 -left-8 text-background-50/10 font-display text-[150px] leading-none select-none">瓜</div>
            <p className="text-xs font-bold tracking-widest text-background-100 mb-2">CONTACT</p>
            <h3 className="font-heading text-2xl md:text-3xl font-black leading-snug">想第一时间吃瓜？<br />订阅或联系我们</h3>
            <p className="mt-3 text-sm text-background-100/90 leading-relaxed">
              填写邮箱即可订阅每日热点精选；也欢迎投稿线索与商务合作，我们会认真对待每一条来信。
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <a href="mailto:editor@example.com" className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-xl bg-background-50/15 flex items-center justify-center"><i className="ri-mail-line"></i></span>
                <span>
                  <span className="block text-xs text-background-100/80">投稿与纠错</span>
                  <span className="font-medium group-hover:underline">editor@example.com</span>
                </span>
              </a>
              <a href="mailto:biz@example.com" className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-xl bg-background-50/15 flex items-center justify-center"><i className="ri-briefcase-line"></i></span>
                <span>
                  <span className="block text-xs text-background-100/80">商务合作</span>
                  <span className="font-medium group-hover:underline">biz@example.com</span>
                </span>
              </a>
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-3 p-6 md:p-10">
            <h4 className="font-heading text-xl font-black text-foreground-950">订阅每日吃瓜精选</h4>
            <p className="mt-1.5 text-sm text-foreground-600">每周一三五早 8 点，把过去 48h 最值得聊的瓜送到你邮箱。随时可退订。</p>

            <form
              data-readdy-form="chigua-subscribe"
              className="mt-6 flex flex-col sm:flex-row gap-3"
              onSubmit={onSubmit}
              noValidate
            >
              <div className="relative flex-1">
                <i className="ri-mail-send-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="输入你的邮箱地址"
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-background-100 border border-background-200/70 focus:outline-none focus:ring-2 focus:ring-primary-400 text-sm text-foreground-950 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition whitespace-nowrap disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <><i className="ri-loader-4-line animate-spin"></i> 提交中</>
                ) : (
                  <>立即订阅 <i className="ri-arrow-right-line"></i></>
                )}
              </button>
              <div className="frm-ghost-wrap" aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>
            </form>

            {status === 'success' && (
              <p className="mt-4 flex items-center gap-2 text-sm font-medium text-accent-700 bg-accent-100 rounded-xl px-4 py-3">
                <i className="ri-checkbox-circle-line text-lg"></i>{message}
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 flex items-start gap-2 text-sm font-medium text-primary-700 bg-primary-100 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-lg"></i>{message}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-2 text-xs text-foreground-600">
              <span className="flex items-center gap-1.5"><i className="ri-lock-line text-accent-600"></i>信息仅用于订阅推送</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line text-accent-600"></i>随时一键退订</span>
              <span className="flex items-center gap-1.5"><i className="ri-shield-check-line text-accent-600"></i>绝不外泄隐私</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}